<template>
  <header class="home-header">
    <div class="home-header-inner">
      <router-link to="/" class="home-logo">
        <img src="@/assets/logo500.png" alt="阿呆云 adaiyun.net" class="home-logo-image">
      </router-link>
      <nav class="home-nav">
        <router-link to="/" class="home-nav-item">首页</router-link>
        <router-link to="/remove-watermark" class="home-nav-item">链接去水印</router-link>
        <router-link to="/ai-image-remove" class="home-nav-item">图片去水印</router-link>
        <router-link to="/video-remove" class="home-nav-item">视频去水印</router-link>
        <router-link to="/remove-bg" class="home-nav-item">人像抠图</router-link>
      </nav>
      <div class="home-header-actions">
        <button type="button" class="home-user-button" :class="{ 'logged-in': userInfo }" @click="onLoginClick">
          <div v-if="userInfo" class="user-info-display">
            <img :src="userInfo.avatar" class="user-avatar-small" />
            <div class="user-copy">
              <span class="user-nickname">{{ formattedNickname }}</span>
              <span class="user-times">剩余 {{ userInfo.times || 0 }} 次</span>
            </div>
          </div>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="8" r="4"></circle>
            <path d="M4 20c1.5-3 4-5 8-5s6.5 2 8 5"></path>
          </svg>
        </button>
      </div>
    </div>
    <teleport to="body">
      <div v-if="showLoginModal" class="wx-login-overlay" @click.self="closeLoginModal">
        <div class="wx-login-container">
          <div class="wx-login-header">
            <div class="wx-login-title">微信扫码登录</div>
            <button class="wx-login-close" @click="closeLoginModal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="wx-login-body">
            <div class="wxlogin-qrcode-box">
              <div v-if="loginQrLoading" class="wxlogin-qrcode-loading">
                <div class="wxlogin-spinner"></div>
                <div class="wxlogin-loading-text">二维码生成中...</div>
              </div>
              <img
                v-else-if="loginQrCode"
                :src="loginQrCode"
                class="wxlogin-qrcode-img"
              />
              <div v-else id="wxlogin-qrcode" class="wxlogin-qrcode"></div>
            </div>
            <div class="wxlogin-hint">请使用手机微信扫描二维码完成登录</div>
          </div>
        </div>
      </div>
      <div v-if="showProfileModal" class="wx-login-overlay" @click.self="showProfileModal = false">
        <div class="wx-login-container profile-container">
          <div class="wx-login-header">
            <div class="wx-login-title">个人资料</div>
            <button class="wx-login-close" @click="showProfileModal = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="profile-body">
            <div class="profile-header-info">
              <img :src="userInfo?.avatar" class="profile-avatar" />
              <div class="profile-text">
                <div class="profile-nickname">{{ userInfo?.nickname }}</div>
                <div class="profile-id">ID: SYF{{ userInfo?.id.toString().padStart(6,'0') }}</div>
              </div>
            </div>
            <div class="profile-details">
              <div class="profile-item">
                <span class="label">会员状态:</span>
                <span class="value" :class="{ 'is-vip': userInfo?.member }">{{ userInfo?.member ? '尊贵会员' : '普通用户' }}</span>
              </div>
              <div class="profile-item" v-if="userInfo?.member">
                <span class="label">到期时间:</span>
                <span class="value">{{ formattedDeadlineTime }}</span>
              </div>
              <div class="profile-item">
                 <span class="label">剩余次数:</span>
                 <span class="value">{{ userInfo?.times }}次</span>
              </div>
            </div>
            <button class="profile-main-btn" @click="goToProfileCenter">进入个人中心</button>
            <button class="vip-btn" @click="goToWebPay">{{ userInfo?.member ? '续费会员' : '开通会员' }}</button>
            <button class="logout-btn" @click="logout">退出登录</button>
          </div>
        </div>
      </div>
    </teleport>
  </header>
</template>

<script>
import request from '@/utils/request'
import { API_BASE_URL } from '@/constants/api'
import { showDialog, showConfirmDialog } from 'vant'
import { formatTime } from '@/utils/date'

export default {
  name: 'AppHeader',
  data() {
    return {
      wxReady: false,
      showLoginModal: false,
      showProfileModal: false,
      userInfo: null,
      loginState: null,
      loginPollTimer: null,
      loginQrCode: null,
      loginQrLoading: false
    }
  },
  mounted() {
    if (localStorage.getItem('token')) {
      this.fetchUserInfo()
    }
    // 监听全局登录事件
    window.addEventListener('auth-login-required', this.handleLoginRequired)
    // 监听全局API错误事件
    window.addEventListener('global-api-error', this.handleGlobalError)
  },
  unmounted() {
    window.removeEventListener('auth-login-required', this.handleLoginRequired)
    window.removeEventListener('global-api-error', this.handleGlobalError)
  },
  computed: {
    isWeChat() {
      return /MicroMessenger/i.test(navigator.userAgent)
    },
    formattedNickname() {
      if (!this.userInfo || !this.userInfo.nickname) return ''
      return this.userInfo.nickname.length > 4 
        ? this.userInfo.nickname.slice(0, 4) + '...' 
        : this.userInfo.nickname
    },
    formattedDeadlineTime() {
      const deadlineTime = this.userInfo && this.userInfo.deadlineTime
      if (!deadlineTime) return ''
      const date = typeof deadlineTime === 'number' || /^\d+$/.test(String(deadlineTime))
        ? new Date(Number(deadlineTime))
        : new Date(deadlineTime)
      if (Number.isNaN(date.getTime())) return String(deadlineTime)
      return formatTime(date)
    }
  },
  methods: {
    handleGlobalError(event) {
      const msg = (event.detail && event.detail.msg) ? event.detail.msg : 'Unknown Error'
      showDialog({
        message: msg,
        confirmButtonText: '确定'
      })
    },
    handleLoginRequired(event) {
      // 清除本地 token 和用户信息
      localStorage.removeItem('token')
      this.userInfo = null
      
      const msg = (event.detail && event.detail.msg) ? event.detail.msg : 'token失效或不可用'

      showConfirmDialog({
        message: msg,
        confirmButtonText: '去登录',
        cancelButtonText: '取消',
      })
      .then(() => {
        // 打开登录弹窗
        this.onLoginClick()
      })
      .catch(() => {
        // 取消
      })
    },
    async onLoginClick() {
      if (this.userInfo) {
        this.fetchUserInfo()
        this.showProfileModal = true
        return
      }
      if (this.isWeChat) {
        try {
          await this.ensureWxReady()
          await this.scanLogin()
        } catch (e) {
          this.openWebQr()
        }
      } else {
        this.openWebQr()
      }
    },
    async ensureWxReady() {
      if (!window.wx) {
        await this.loadWxScript('https://res.wx.qq.com/open/js/jweixin-1.6.0.js')
      }
      if (!this.wxReady) {
        const url = encodeURIComponent(window.location.href.split('#')[0])
        const cfg = await request(`/wechat/jsconfig?url=${url}`, { method: 'GET' })
        window.wx.config({
          debug: false,
          appId: cfg.appId,
          timestamp: cfg.timestamp,
          nonceStr: cfg.nonceStr,
          signature: cfg.signature,
          jsApiList: ['scanQRCode']
        })
        await new Promise((resolve, reject) => {
          window.wx.ready(() => {
            this.wxReady = true
            resolve()
          })
          window.wx.error(() => reject(new Error('wx config error')))
        })
      }
    },
    loadWxScript(src) {
      return new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = src
        s.onload = () => resolve()
        s.onerror = () => reject(new Error('wx script load error'))
        document.head.appendChild(s)
      })
    },
    scanLogin() {
      return new Promise((resolve, reject) => {
        window.wx.scanQRCode({
          needResult: 1,
          scanType: ['qrCode'],
          success: async (res) => {
            const result = res.resultStr || ''
            try {
              await request('/user/wechat/scanLogin', {
                method: 'POST',
                contentType: 'application/json',
                body: { result }
              })
              resolve()
            } catch {
              reject(new Error('scan login failed'))
            }
          },
          fail: () => reject(new Error('scan failed'))
        })
      })
    },
    async openWebQr() {
      this.showLoginModal = true
      this.loginQrCode = null
      this.loginQrLoading = true
      try {
        const res = await request('/user/wx/web/qrcode', { method: 'GET' })
        if (res && res.image) {
          this.loginQrCode = res.image
          this.loginState = res.scene
          this.startLoginPolling()
          this.loginQrLoading = false
        } else {
          // Fallback to old method if new one fails or returns unexpected structure
          this.openOldWebQr()
        }
      } catch (e) {
        console.error('New web login failed, falling back to old', e)
        this.openOldWebQr()
      }
    },
    async openOldWebQr() {
      this.showLoginModal = true
      this.loginQrLoading = true
      await this.loadWxLoginScript()
      // TODO: 这里必须使用 微信开放平台(open.weixin.qq.com) 的 网站应用 AppID
      // 如果使用 公众号(mp.weixin.qq.com) 的 AppID，会报错 "scope unauthorized"
      const appid = 'wx085b87fec2411087' 
      const state = Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
      this.loginState = state
      const redirectUri = encodeURIComponent(`${API_BASE_URL}/user/wx/web/callback`)
      // eslint-disable-next-line no-new
      new window.WxLogin({
        id: 'wxlogin-qrcode',
        appid,
        scope: 'snsapi_login',
        redirect_uri: redirectUri,
        state,
        style: 'black'
      })
      this.loginQrLoading = false
      this.startLoginPolling(true)
    },
    async loadWxLoginScript() {
      if (window.WxLogin) return
      await new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'
        s.onload = () => resolve()
        s.onerror = () => reject(new Error('wxLogin script load error'))
        document.head.appendChild(s)
      })
    },
    startLoginPolling(isOld = false) {
      if (this.loginPollTimer) {
        clearInterval(this.loginPollTimer)
      }
      this.loginPollTimer = setInterval(async () => {
        try {
          if (isOld) {
            const res = await request(`/user/wx/web/status?state=${this.loginState}`, { method: 'GET' })
            if (res && res.token) {
              localStorage.setItem('token', res.token)
              this.closeLoginModal()
              window.location.reload()
            }
          } else {
            request(`/user/wx/web/status?scene=${this.loginState}`, { method: 'GET' })
            .then(res => {
              if (res && res.status === 'CONFIRMED') {
                localStorage.setItem('token', res.token)
                localStorage.setItem('session', res.session)
                this.closeLoginModal()
                if (res.userInfo) {
                  this.userInfo = res.userInfo
                } else {
                  this.fetchUserInfo()
                } 
              }
            })
            .catch((err)=>{
              showConfirmDialog({
                title: '登录失败',
                message: err.message,
                confirmText: '重新登录',
                cancelText: '关闭'
              })
              this.closeLoginModal()
            })
          }
        } catch (e) {
          console.error(e)
        }
      }, 2000)
    },
    closeLoginModal() {
      this.showLoginModal = false
      if (this.loginPollTimer) {
        clearInterval(this.loginPollTimer)
        this.loginPollTimer = null
      }
      this.loginState = null
      this.loginQrLoading = false
    },
    async fetchUserInfo() {
      try {
        const res = await request('/user/info', { method: 'GET' })
        if (res) {
          this.userInfo = res
        }
      } catch (e) {
        console.error('Fetch user info failed', e)
        localStorage.removeItem('token')
        this.userInfo = null
      }
    },
    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('session')
      this.userInfo = null
      this.showProfileModal = false
      window.location.reload()
    },
    navigateFromProfile(path) {
      this.showProfileModal = false
      this.$router.push(path)
    },
    goToWebPay() {
      this.navigateFromProfile('/web-pay')
    },
    goToProfileCenter() {
      this.navigateFromProfile('/profile')
    },
    goToCurrencyRecord() {
      this.navigateFromProfile('/currency-record')
    },
    goToCommonQuestion() {
      this.navigateFromProfile('/common-question')
    },
    goToTutorial() {
      this.navigateFromProfile('/tutorial')
    },
    goToServiceStatement() {
      this.navigateFromProfile('/service-statement')
    }
  }
}
</script>

<style>
.home-header {
  position: sticky;
  top: 0;
  z-index: 60;
  margin-bottom: 26px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(223, 230, 242, 0.92);
  box-shadow: 0 6px 18px rgba(50, 71, 117, 0.04);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.home-header-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 18px 28px;
}

.home-logo {
  display: flex;
  align-items: center;
}

.home-logo-image {
  height: 42px;
  display: block;
}

.home-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  justify-content: center;
  min-width: 0;
  flex-wrap: nowrap;
  padding: 6px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(244, 247, 252, 0.96) 0%, rgba(255, 255, 255, 0.92) 100%);
  border: 1px solid rgba(223, 230, 242, 0.92);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.home-nav-item {
  border: none;
  background: transparent;
  color: #61738d;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  padding: 11px 15px;
  border-radius: 999px;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.home-nav-item.router-link-exact-active,
.home-nav-item:hover {
  color: var(--brand-deep);
  background: rgba(79, 70, 229, 0.08);
  transform: translateY(-1px);
}

.home-nav-item.router-link-exact-active {
  color: #19284a;
  background: linear-gradient(180deg, #ffffff 0%, #f6f8fd 100%);
  box-shadow: 0 10px 18px rgba(50, 71, 117, 0.08);
}

.home-header-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.home-user-button {
  min-width: 112px;
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(223, 230, 242, 0.92);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94) 0%, rgba(246, 248, 253, 0.92) 100%);
  color: #213454;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.home-user-button:hover {
  transform: translateY(-1px);
  border-color: rgba(79, 70, 229, 0.18);
  box-shadow: 0 16px 26px rgba(50, 71, 117, 0.12);
}

.home-user-button svg {
  width: 20px;
  height: 20px;
}

.wx-login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}
.wx-login-container {
  width: 360px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.88);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.wx-login-header {
  padding: 16px 18px;
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.wx-login-title {
  font-weight: 700;
  color: var(--ink);
}
.wx-login-close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--muted);
}
.wx-login-close svg {
  width: 20px;
  height: 20px;
}
.wx-login-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.wxlogin-qrcode-box {
  width: 280px;
  height: 280px;
  padding: 10px;
  box-sizing: border-box;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(180deg, #ffffff 0%, #f6f8fc 100%);
}

.wxlogin-qrcode {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
}

.wxlogin-qrcode-loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.wxlogin-qrcode-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.wxlogin-spinner {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 3px solid rgba(79, 70, 229, 0.25);
  border-top-color: #4f46e5;
  animation: wxlogin-spin 0.9s linear infinite;
}
.wxlogin-loading-text {
  font-size: 12px;
  color: var(--muted);
}
@keyframes wxlogin-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.wxlogin-hint {
  font-size: 12px;
  color: var(--muted);
}

.home-user-button.logged-in {
  width: auto;
  height: auto;
  min-width: 168px;
  justify-content: flex-start;
  padding: 6px 12px 6px 6px;
  gap: 10px;
}

.home-user-button.logged-in:hover {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 247, 252, 0.96) 100%);
}

.user-info-display {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.user-avatar-small {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 18px rgba(50, 71, 117, 0.14);
}
.user-nickname {
  max-width: 104px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--ink);
  line-height: 1.1;
}

.user-times {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-deep);
  background: rgba(79, 70, 229, 0.08);
}

.profile-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.profile-header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}
.profile-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(79, 70, 229, 0.12);
}
.profile-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.profile-nickname {
  font-size: 18px;
  font-weight: 600;
  color: var(--ink);
}
.profile-id {
  font-size: 13px;
  color: var(--muted);
}
.profile-details {
  background: linear-gradient(180deg, #ffffff 0%, #f5f8fc 100%);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.profile-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.profile-item .label {
  color: var(--muted);
}
.profile-item .value {
  color: var(--ink);
  font-weight: 500;
}
.profile-item .value.is-vip {
  color: #d97706;
}
.profile-main-btn {
  width: 100%;
  padding: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f4f7ff 100%);
  color: var(--ink);
  border: 1px solid rgba(214, 224, 238, 0.95);
  border-radius: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}
.profile-main-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(113, 124, 245, 0.28);
  box-shadow: 0 12px 24px rgba(65, 84, 129, 0.06);
}
.logout-btn {
  width: 100%;
  padding: 12px;
  background: #fff3f1;
  color: #e11d48;
  border: none;
  border-radius: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}
.logout-btn:hover {
  background: #ffe4e6;
}

.vip-btn {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #5b6bff 0%, #2f80ed 100%);
  color: #ffffff;
  border: none;
  border-radius: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.vip-btn:hover {
  filter: brightness(0.98);
}

@media (max-width: 980px) {
  .home-header-inner {
    flex-wrap: wrap;
    padding: 16px 20px;
  }

  .home-nav {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 6px;
  }
}

@media (max-width: 640px) {
  .home-user-button {
    min-width: 48px;
    padding: 0 14px;
  }

  .home-user-button.logged-in {
    min-width: auto;
    padding: 6px;
  }

  .user-copy {
    display: none;
  }
}
</style>
